import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { marketApi } from '../Market/api';

const Page = styled.main`
  max-width: 1100px; margin: auto; padding: 24px; color: #172033;
  h1 { margin: 0 0 8px; } p { line-height: 1.6; }
  .tools, .actions { display: flex; flex-wrap: wrap; gap: 12px; margin: 18px 0; }
  input, select, textarea, button { font: inherit; padding: 10px 14px; border: 1px solid #ccd6e4; border-radius: 8px; background: white; }
  button { cursor: pointer; } button:disabled { opacity: .5; cursor: default; }
  .primary { background: #2463eb; color: white; }
  .rows { display: grid; gap: 12px; }
  article { background: white; border: 1px solid #dae3ed; border-radius: 12px; padding: 18px; }
  .row { display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap; }
  .error { padding: 14px; background: #fff1f2; color: #9f1239; }
  .detail { white-space: pre-wrap; overflow-wrap: anywhere; }
  textarea { width: 100%; box-sizing: border-box; min-height: 90px; }
  a { overflow-wrap: anywhere; }
`;
const labels = { draft: '등록 대기', published: '승인·공개', archived: '반려' };
const date = value => value ? new Date(value).toLocaleString('ko-KR') : '날짜 없음';
const safeSource = source => { try { const u = new URL(source); return ['https:', 'http:'].includes(u.protocol) ? u.href : null; } catch (_) { return null; } };

export default function AgentSubmissions() {
  const [status, setStatus] = useState('draft');
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState({ items: [], hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setSelected(null);
    marketApi(`/agent-submissions?${new URLSearchParams({ status, query: search, page })}`)
      .then(result => { if (active) setData(result); })
      .catch(err => { if (active) { setError(err.message); setData({ items: [], hasMore: false }); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [status, search, page, revision]);
  useEffect(() => {
    let active = true;
    setEvidence(null); setNote('');
    if (selected) marketApi(`/offerings/${selected.prod_id}/research`)
      .then(rows => { if (active) setEvidence(rows); })
      .catch(err => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [selected]);
  async function review(next) {
    if (!selected || busy || (next === 'archived' && !note.trim())) return;
    setBusy(true); setError(''); setMessage('');
    try {
      await marketApi(`/offerings/${selected.prod_id}/review`, { status: next, note: note.trim(), expectedUpdatedAt: selected.updated_at });
      setMessage(`${selected.prod_name}: ${labels[next]} 처리했습니다.`);
      setSelected(null); setRevision(v => v + 1);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  return <Page>
    <h1>에이전트 제품 등록 대기열</h1>
    <p>내 계정의 에이전트가 제출한 제품을 검토합니다. 승인한 제품만 스토어에 공개됩니다.</p>
    <form className="tools" onSubmit={e => { e.preventDefault(); setPage(0); setSearch(query); setRevision(v => v + 1); }}>
      <input aria-label="제품명 또는 에이전트 검색" placeholder="제품명 · 에이전트 검색" value={query} maxLength={100} onChange={e => setQuery(e.target.value)} disabled={busy} />
      <select aria-label="등록 상태" value={status} disabled={busy} onChange={e => { setStatus(e.target.value); setPage(0); }}>
        {Object.entries({ ...labels, all: '전체' }).map(([key, value]) => <option key={key} value={key}>{value}</option>)}
      </select>
      <button disabled={busy}>검색</button>
      <button type="button" disabled={busy || loading} onClick={() => setRevision(v => v + 1)}>새로고침</button>
    </form>
    {error && <p role="alert" className="error">{error} <a href="/Login" target="_blank" rel="noreferrer">스토어 로그인</a></p>}
    {message && <p role="status">{message}</p>}
    {loading ? <p role="status">제품 대기열을 불러오는 중…</p> : <>
      {!error && data.items.length === 0 && <p>해당 조건의 제출 제품이 없습니다.</p>}
      <div className="rows">{data.items.map(item => <article key={item.prod_id} className="row">
        <div><strong>{item.prod_name}</strong><p>{item.author_name || '에이전트'} · {item.prod_category || '분류 미지정'} · {Number(item.prod_price || 0).toLocaleString()}원</p>
          <small>{labels[item.status] || item.status} · 수정 {date(item.updated_at)}</small></div>
        <button disabled={busy} onClick={() => { setError(''); setSelected(item); }}>상세 검토</button>
      </article>)}</div>
      <div className="actions"><button disabled={!page || busy} onClick={() => setPage(v => v - 1)}>이전</button><span>{page + 1} 페이지</span><button disabled={!data.hasMore || busy} onClick={() => setPage(v => v + 1)}>다음</button></div>
    </>}
    {selected && <article aria-label="제품 상세 검토">
      <h2>{selected.prod_name}</h2><p className="detail">{selected.prod_desc}</p>
      <p>종류: {selected.offer_kind || '미지정'} · 제공 기간: {selected.duration || '미지정'}</p>
      <h3>에이전트 조사 근거</h3>
      {evidence === null ? <p>근거 확인 중…</p> : <><p>확인 가능한 출처 {evidence.filter(e => safeSource(e.source)).length}건 · 승인 시 최소 3건 필요</p>
        {evidence.map(e => <p key={e.id}><strong>{e.topic}</strong><br />{e.finding}<br />{safeSource(e.source) && <a href={safeSource(e.source)} target="_blank" rel="noopener noreferrer">{e.source}</a>}</p>)}</>}
      {selected.review_note && <p>이전 검토 의견: {selected.review_note}</p>}
      <label>검토 의견 (반려 시 필수)<textarea value={note} maxLength={2000} disabled={busy} onChange={e => setNote(e.target.value)} /></label>
      <div className="actions">
        <button className="primary" disabled={busy || selected.status !== 'draft' || evidence === null || evidence.filter(e => safeSource(e.source)).length < 3} onClick={() => review('published')}>승인하고 공개</button>
        <button disabled={busy || selected.status !== 'draft' || !note.trim()} onClick={() => review('archived')}>반려</button>
        <button disabled={busy} onClick={() => setSelected(null)}>닫기</button>
      </div>
    </article>}
  </Page>;
}
